package com.writerslab

import kotlinx.browser.localStorage
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/** A checkout the user was sent off to pay; see [AppDataStore.setPendingPayment]. */
@Serializable
data class PendingPayment(
    val id: String,
    val packId: String,
    /** Provider's payment page — the banner's «back to payment». Empty
     *  when only the id is known (came back from another tab's checkout). */
    val redirectUrl: String,
    val createdAtMs: Double,
    /** The user closed the banner: keep it closed across reloads, and no
     *  «canceled» toast when the invoice expires. */
    val quiet: Boolean = false,
)

/**
 * localStorage-backed key-value storage. Wraps the browser API directly via
 * kotlinx.browser; no extra interop layer needed on Kotlin/JS.
 */
object AppDataStore {

    // --- Tokens ---

    fun saveTokens(accessToken: String, idToken: String?, refreshToken: String?) {
        localStorage.setItem("wl_token", accessToken)
        if (idToken != null) localStorage.setItem("wl_id_token", idToken)
        if (refreshToken != null) localStorage.setItem("wl_refresh_token", refreshToken)
        localStorage.setItem("wl_logged_in", "true")
    }

    fun updateAccessToken(accessToken: String, refreshToken: String?) {
        localStorage.setItem("wl_token", accessToken)
        if (refreshToken != null) localStorage.setItem("wl_refresh_token", refreshToken)
    }

    fun getAccessToken(): String? = localStorage.getItem("wl_token")

    fun getIdToken(): String? = localStorage.getItem("wl_id_token")

    fun getRefreshToken(): String? = localStorage.getItem("wl_refresh_token")

    fun clearTokens() {
        localStorage.removeItem("wl_token")
        localStorage.removeItem("wl_id_token")
        localStorage.removeItem("wl_refresh_token")
        localStorage.removeItem("wl_logged_in")
        localStorage.removeItem("wl_user_id")
        clearPendingPayment()
        clearAllPromoFields()
    }

    // --- User id ---
    //
    // Cached server-side user id from the most recent /me response. Kept
    // separately from the tokens because it's the identity the *backend*
    // assigns (not Keycloak's `sub`), and it's what gates [UserCache] so
    // that one browser switching between accounts can't briefly paint the
    // previous user's snapshot onto the new user's screen.

    fun setUserId(id: String) {
        localStorage.setItem("wl_user_id", id)
    }

    fun getUserId(): String? = localStorage.getItem("wl_user_id")

    fun clearUserId() {
        localStorage.removeItem("wl_user_id")
    }

    // --- Unsaved reader edits ---
    //
    // One book's unsaved edits — the whole manuscript. Dropped on logout:
    // the next person at this browser mustn't find them.

    const val EDIT_DRAFT_KEY = "wl_edit_draft"

    fun clearEditDraft() {
        localStorage.removeItem(EDIT_DRAFT_KEY)
    }

    // --- Analysis state ---

    fun saveAnalysisState(bookId: String, fileName: String, analysisJson: String) {
        localStorage.setItem("wl_bookId", bookId)
        localStorage.setItem("wl_fileName", fileName)
        localStorage.setItem("wl_analysis", analysisJson)
    }

    fun getSavedBookId(): String? = localStorage.getItem("wl_bookId")
    fun getSavedFileName(): String? = localStorage.getItem("wl_fileName")
    fun getSavedAnalysisJson(): String? = localStorage.getItem("wl_analysis")

    fun clearAnalysisState() {
        localStorage.removeItem("wl_bookId")
        localStorage.removeItem("wl_fileName")
        localStorage.removeItem("wl_analysis")
    }

    // --- Active book pointer ---
    // Separate key from `wl_bookId` (which is part of the sign-in handoff
    // bundle and gets cleared after restore). This one is the lightweight
    // "what was I looking at last?" pointer used to survive a plain page
    // refresh: we save just the fingerprint, then on next load look it up
    // in the fresh `bookHistory` and replay `selectFromHistory` on hit.
    fun setCurrentBookId(bookId: String) {
        localStorage.setItem("wl_currentBookId", bookId)
    }

    fun getCurrentBookId(): String? = localStorage.getItem("wl_currentBookId")

    fun clearCurrentBookId() {
        localStorage.removeItem("wl_currentBookId")
    }

    // --- Anonymous per-book read token ---
    //
    // The server hands an anon uploader a per-book capability token in the
    // /analyze response; the reader sends it back as the `X-Book-Token` header
    // to fetch the text / highlights of a book it uploaded without a login.
    // Keyed per bookId and persisted so it survives a page refresh (the reader
    // can reload the book from cache). Logged-in reads use the JWT instead, so
    // this is only ever set/consumed while anonymous.

    private const val ANON_TOKEN_PREFIX = "wl_anonTok_"

    fun setAnonReadToken(bookId: String, token: String) {
        localStorage.setItem(ANON_TOKEN_PREFIX + bookId, token)
    }

    fun getAnonReadToken(bookId: String): String? =
        localStorage.getItem(ANON_TOKEN_PREFIX + bookId)

    fun clearAnonReadToken(bookId: String) {
        localStorage.removeItem(ANON_TOKEN_PREFIX + bookId)
    }

    // --- Anonymous EULA consent ---

    fun isEulaAccepted(): Boolean = localStorage.getItem("wl_eula") == "true"

    fun acceptEula() {
        localStorage.setItem("wl_eula", "true")
    }

    // --- Promo form inputs (title / author per book) ---
    //
    // Persists the title + author the user typed into the promo sidebar
    // so a page refresh — or closing and re-opening the browser — doesn't
    // wipe them. Without this a refresh while a generation job is mid-
    // flight costs the user their inputs AND the credits already spent.
    //
    // Keyed per bookId because the fields are book-scoped. Cleared en
    // masse on logout (see [clearTokens]) so the next account on this
    // browser doesn't see the previous user's typed inputs.

    private const val PROMO_TITLE_PREFIX = "wl_promo_title_"
    private const val PROMO_AUTHOR_PREFIX = "wl_promo_author_"

    fun setPromoTitle(bookId: String, value: String) {
        localStorage.setItem(PROMO_TITLE_PREFIX + bookId, value)
    }

    fun getPromoTitle(bookId: String): String? =
        localStorage.getItem(PROMO_TITLE_PREFIX + bookId)

    fun setPromoAuthor(bookId: String, value: String) {
        localStorage.setItem(PROMO_AUTHOR_PREFIX + bookId, value)
    }

    fun getPromoAuthor(bookId: String): String? =
        localStorage.getItem(PROMO_AUTHOR_PREFIX + bookId)

    private fun clearAllPromoFields() {
        // Collect first, delete after — removing while iterating shifts
        // the indices in browser localStorage.
        val keys = mutableListOf<String>()
        for (i in 0 until localStorage.length) {
            val k = localStorage.key(i) ?: continue
            if (k.startsWith(PROMO_TITLE_PREFIX) || k.startsWith(PROMO_AUTHOR_PREFIX)) {
                keys.add(k)
            }
        }
        keys.forEach { localStorage.removeItem(it) }
    }

    // --- Pricing region override ---
    // 'ru' or 'intl'. null means "follow UI language" — RU language → ru,
    // anything else → intl. Set explicitly via the toggle in TopUpDialog.

    fun getPricingRegion(): String? = localStorage.getItem("wl_pricing_region")

    fun setPricingRegion(region: String) {
        localStorage.setItem("wl_pricing_region", region)
    }

    // --- Pending checkout ---
    // The payment the user last left for, so the app can keep watching it
    // after Back from the payment page, a reload, or a visit hours later.
    // One at a time: a new checkout replaces it.

    fun setPendingPayment(p: PendingPayment) {
        localStorage.setItem("wl_pending_payment", Json.encodeToString(PendingPayment.serializer(), p))
    }

    fun getPendingPayment(): PendingPayment? =
        localStorage.getItem("wl_pending_payment")?.let {
            runCatching { Json.decodeFromString(PendingPayment.serializer(), it) }.getOrNull()
        }

    fun clearPendingPayment() {
        localStorage.removeItem("wl_pending_payment")
    }

    // --- Payments already announced ---
    // Ids of the last payments this browser showed an outcome for, so a late
    // message from the payment window or a second tab doesn't announce (and
    // report revenue for) the same payment again.

    fun markPaymentSettled(id: String) {
        val ids = (settledPaymentIds() - id + id).takeLast(20)
        localStorage.setItem("wl_settled_payments", ids.joinToString(","))
    }

    fun isPaymentSettled(id: String): Boolean = id in settledPaymentIds()

    private fun settledPaymentIds(): List<String> =
        localStorage.getItem("wl_settled_payments")?.split(",")?.filter { it.isNotBlank() }.orEmpty()
}
